import { Box, Button, Grid, InputLabel, MenuItem, Select, TextField } from '@mui/material'
import { SectionHeader } from '@react/components/common/SectionHeader'
import { fetchCollateral, saveCollateral } from '@react/fetchers/collateralFetchers'
import { useOtChainState, useOtSelector } from '@react/redux/store'
import {
  CollateralSetCTO,
  DEFAULT_ASSET_TOLERANCE,
  DEFAULT_RESOLVED_VARIANCE,
  DefiCollateralCTO,
  DefiType,
  ExchangeRateAutomation,
  MAX_EXCHANGE_RATE_AUTO_CHANGE,
  MIN_EXCHANGE_RATE_AUTO_CHANGE,
} from '@shared/CollateralSetCTO'
import { ERC20Wei } from '@shared/conversions'

import { getDefiData, getDefiNameAndSymbol } from '@shared/defiVaultUtils'
import {
  NONPROD_CALIBRATION_WALLET_ADDR,
  NONPROD_FEE_VAULT_ADDR,
  NONPROD_SIMULATED_TRANSFER_WALLET_ADDR,
  NONPROD_SIMULATED_VAULT_ADDR,
} from '@shared/DeploymentConfigsDefaults'
import {
  BASE_USDC_ADDR,
  MAINNET_USDC_ADDR,
  PROD_CALIBRATION_WALLET_ADDR,
  PROD_DEFI_TRANSFER_WALLET_ADDR,
  PROD_DEFI_VAULT_ADDR_AAVE,
  PROD_DEFI_VAULT_ADDR_MORPHO,
  PROD_DEFI_VAULT_ADDR_MORPHO_BASE,
  PROD_DEFI_VAULT_ADDR_WILDCAT,
  PROD_FEE_VAULT_ADDR,
} from '@shared/DeploymentConfigsProd'
import { ChainId, EXCHANGE_RATE_ONE } from '@shared/Enums'
import { logOT } from '@shared/loggingShared'
import { EthAddr } from '@shared/SchemaTypes'
import { ethers, ZeroAddress } from 'ethers'
import React, { useEffect, useState } from 'react'

type Props = {
  poolAddr: EthAddr
  onClose: () => void
}

export default function DefiCollateralForm({ poolAddr, onClose }: Props): React.ReactElement {
  const {
    stageConfiguration: { serverApiBaseUrl, restAPIKey, stageName },
    cognitoUser,
  } = useOtChainState()

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isChangingType, setIsChangingType] = useState(false)
  const [calibrationWalletAddr, setCalibrationWalletAddr] = useState<EthAddr>(NONPROD_CALIBRATION_WALLET_ADDR)
  const [defiChainId, setDefiChainId] = useState<ChainId>(ChainId.MAINNET)
  const [defiType, setDefiType] = useState<DefiType>(DefiType.Undefined)
  const [defiVaultAddr, setDefiVaultAddr] = useState(ZeroAddress)
  const [defiVaultSymbol, setDefiVaultSymbol] = useState<string>('')
  const [defiVaultName, setDefiVaultName] = useState<string>('')
  const [feeVaultWalletAddr, setFeeVaultWalletAddr] = useState<EthAddr>(
    stageName?.startsWith('local') ? NONPROD_FEE_VAULT_ADDR : PROD_FEE_VAULT_ADDR
  )
  const [defiNativeURL, setDefiNativeURL] = useState<string>('')
  const [assetTolerance, setAssetTolerance] = useState<ERC20Wei>(DEFAULT_ASSET_TOLERANCE.toString() as ERC20Wei)
  const [exchangeRateAutomation, setExchangeRateAutomation] = useState<ExchangeRateAutomation>(
    ExchangeRateAutomation.Manual
  )
  const [exchangeRateMaximumAutoChange, setExchangeRateMaximumAutoChange] = useState<ERC20Wei>(
    MAX_EXCHANGE_RATE_AUTO_CHANGE.toString() as ERC20Wei
  )
  const [exchangeRateMinimumMaterialChange, setExchangeRateMinimumMaterialChange] = useState<ERC20Wei>(
    MIN_EXCHANGE_RATE_AUTO_CHANGE.toString() as ERC20Wei
  )

  const [liquidityAssetAddr, setLiquidityAssetAddr] = useState<EthAddr>(MAINNET_USDC_ADDR)
  const [nativeDefiExchangeRateScale, setNativeDefiExchangeRateScale] = useState<ERC20Wei>(
    EXCHANGE_RATE_ONE.toString() as ERC20Wei
  )
  const [resolvedVariance, setResolvedVariance] = useState<ERC20Wei>(DEFAULT_RESOLVED_VARIANCE.toString() as ERC20Wei)

  const [transferWalletAddr, setTransferWalletAddr] = useState<EthAddr>(ZeroAddress)

  const [uuid, setUuid] = useState<string>(ethers.uuidV4(ethers.randomBytes(16)))
  const poolOverviewCTO = useOtSelector((state) => state.poolState.poolOverviewCTOs[poolAddr])

  useEffect(() => {
    if (defiType === DefiType.Undefined) {
      setTransferWalletAddr('')
      setDefiVaultAddr('')
      setDefiVaultSymbol('')
      setDefiVaultName('')
      setCalibrationWalletAddr(NONPROD_CALIBRATION_WALLET_ADDR)
      setLiquidityAssetAddr(MAINNET_USDC_ADDR)
      setExchangeRateAutomation(ExchangeRateAutomation.Manual)
      setDefiNativeURL('')
    }

    if (defiType !== DefiType.Undefined) {
      if (defiType === DefiType.Aave) {
        setDefiVaultAddr(PROD_DEFI_VAULT_ADDR_AAVE)
        setTransferWalletAddr(PROD_DEFI_TRANSFER_WALLET_ADDR)
        setCalibrationWalletAddr(PROD_CALIBRATION_WALLET_ADDR)
        setLiquidityAssetAddr(MAINNET_USDC_ADDR)
        setExchangeRateAutomation(ExchangeRateAutomation.Manual)
        setDefiChainId(ChainId.MAINNET)
        setDefiNativeURL('https://app.aave.com/dashboard/')
      } else if (defiType === DefiType.Morpho) {
        setDefiVaultAddr(PROD_DEFI_VAULT_ADDR_MORPHO)
        setTransferWalletAddr(PROD_DEFI_TRANSFER_WALLET_ADDR)
        setCalibrationWalletAddr(PROD_CALIBRATION_WALLET_ADDR)
        setExchangeRateAutomation(ExchangeRateAutomation.Manual)
        setLiquidityAssetAddr(MAINNET_USDC_ADDR)
        setDefiChainId(ChainId.MAINNET)
        setDefiNativeURL(
          'https://app.morpho.org/ethereum/vault/0xdd0f28e19C1780eb6396170735D45153D261490d/gauntlet-usdc-prime'
        )
      } else if (defiType === DefiType.MorphoBase) {
        setDefiVaultAddr(PROD_DEFI_VAULT_ADDR_MORPHO_BASE)
        setTransferWalletAddr(PROD_DEFI_TRANSFER_WALLET_ADDR)
        setCalibrationWalletAddr(PROD_CALIBRATION_WALLET_ADDR)
        setExchangeRateAutomation(ExchangeRateAutomation.Manual)
        setLiquidityAssetAddr(BASE_USDC_ADDR)
        setDefiChainId(ChainId.BASE)
        setDefiNativeURL(
          'https://app.morpho.org/base/vault/0xeE8F4eC5672F09119b96Ab6fB59C27E1b7e44b61/gauntlet-usdc-prime'
        )
      } else if (defiType === DefiType.Wildcat) {
        setDefiVaultAddr(PROD_DEFI_VAULT_ADDR_WILDCAT)
        setTransferWalletAddr(PROD_DEFI_TRANSFER_WALLET_ADDR)
        setCalibrationWalletAddr(PROD_CALIBRATION_WALLET_ADDR)
        setExchangeRateAutomation(ExchangeRateAutomation.Manual)
        setLiquidityAssetAddr(MAINNET_USDC_ADDR)
        setDefiChainId(ChainId.MAINNET)
        setDefiNativeURL('https://app.wildcat.finance/lender/market/0xc9499006a149c553d18171747ed19aa7c6dd19e2')
      } else if (defiType === DefiType.SIMULATED) {
        setDefiVaultAddr(NONPROD_SIMULATED_VAULT_ADDR)
        setTransferWalletAddr(NONPROD_SIMULATED_TRANSFER_WALLET_ADDR)
        setExchangeRateAutomation(ExchangeRateAutomation.Manual)
        setLiquidityAssetAddr(
          process.env.stage === 'local' ? '0xa9037cAB8B1D264635420b4c7C7EC0307B3D7a65' : MAINNET_USDC_ADDR
        )
        setDefiChainId(ChainId.HARDHAT)
      }
    }
  }, [defiType])

  useEffect(() => {
    if (poolOverviewCTO && defiType !== DefiType.Undefined && transferWalletAddr !== '' && defiVaultAddr !== '') {
      const collateralCTO: DefiCollateralCTO = {
        feeVaultWalletAddr,
        defiType,
        defiVaultAddr,
        otVaultAddr: poolAddr,
        transferWalletAddr,
        defiVaultSymbol: 'T1',
        defiVaultName: 'TEST',
        liquidityAssetAddr,
        calibrationWalletAddr,
        uuid,
        defiChainId,
        nativeDefiExchangeRateScale,
        exchangeRateMaximumAutoChange,
        exchangeRateMinimumMaterialChange,
        resolvedVariance,
        assetTolerance,
        defiNativeURL,
      }
      setIsChangingType(true)

      getDefiData(collateralCTO)
        .then((defiData) => {
          const _nativeDefiExchangeRate = BigInt(defiData.nativeDefiExchangeRateBeforeFees)
          const _primaryExchangeRate = BigInt(defiData.calibratedExchangeRateBeforeFees)
          const _nativeDefiExchangeRateScale =
            _nativeDefiExchangeRate > 0n ? (_primaryExchangeRate * EXCHANGE_RATE_ONE) / _nativeDefiExchangeRate : 0n
          logOT.info('nativeDefiExchangeRateScale', {
            _nativeDefiExchangeRate,
            _primaryExchangeRate,
            _nativeDefiExchangeRateScale,
          })
          setNativeDefiExchangeRateScale(_nativeDefiExchangeRateScale.toString() as ERC20Wei)

          return getDefiNameAndSymbol(collateralCTO)
        })
        .then((nameSymbol) => {
          setDefiVaultSymbol(nameSymbol.symbol)
          setDefiVaultName(nameSymbol.name)
          setIsChangingType(false)
        })
        .catch((error) => {
          logOT.error('Error fetching defi data:', error)
          setIsChangingType(false)
        })
        .finally(() => {
          setIsChangingType(false)
        })
    }
  }, [defiVaultAddr, transferWalletAddr])

  const fetchAndSetCollateral = () => {
    if (poolAddr && serverApiBaseUrl && restAPIKey) {
      setIsChangingType(true)
      fetchCollateral(serverApiBaseUrl, restAPIKey, poolAddr).then((_collateralSetCTO) => {
        setIsChangingType(false)
        if (_collateralSetCTO) {
          setExchangeRateAutomation(_collateralSetCTO.exchangeRateAutomation ?? ExchangeRateAutomation.Manual)
          const defiCollateralCTO = _collateralSetCTO.collateral[0] as DefiCollateralCTO
          if (defiCollateralCTO.feeVaultWalletAddr) {
            setFeeVaultWalletAddr(defiCollateralCTO.feeVaultWalletAddr)
          }
          setCalibrationWalletAddr(defiCollateralCTO.calibrationWalletAddr)
          setDefiChainId(defiCollateralCTO.defiChainId)
          setDefiType(defiCollateralCTO.defiType)
          setDefiVaultAddr(defiCollateralCTO.defiVaultAddr)
          setDefiVaultName(defiCollateralCTO.defiVaultName)
          setDefiVaultSymbol(defiCollateralCTO.defiVaultSymbol)
          setExchangeRateMaximumAutoChange(defiCollateralCTO.exchangeRateMaximumAutoChange)
          setExchangeRateMinimumMaterialChange(defiCollateralCTO.exchangeRateMinimumMaterialChange)
          setLiquidityAssetAddr(defiCollateralCTO.liquidityAssetAddr)
          setNativeDefiExchangeRateScale(defiCollateralCTO.nativeDefiExchangeRateScale)
          setResolvedVariance(defiCollateralCTO.resolvedVariance)
          setTransferWalletAddr(defiCollateralCTO.transferWalletAddr)
          setUuid(defiCollateralCTO.uuid)
          setAssetTolerance(defiCollateralCTO.assetTolerance)
          if ((defiCollateralCTO.defiNativeURL?.length ?? 0) > 0) {
            setDefiNativeURL(defiCollateralCTO.defiNativeURL ?? '')
          }
          if (defiCollateralCTO.defiType === DefiType.Aave) {
            setDefiNativeURL('https://app.aave.com/dashboard/')
          } else if (defiCollateralCTO.defiType === DefiType.Morpho) {
            setDefiNativeURL(
              'https://app.morpho.org/ethereum/vault/0xdd0f28e19C1780eb6396170735D45153D261490d/gauntlet-usdc-prime'
            )
          } else if (defiCollateralCTO.defiType === DefiType.MorphoBase) {
            setDefiNativeURL(
              'https://app.morpho.org/base/vault/0xeE8F4eC5672F09119b96Ab6fB59C27E1b7e44b61/gauntlet-usdc-prime'
            )
          } else if (defiCollateralCTO.defiType === DefiType.Wildcat) {
            setDefiNativeURL('https://app.wildcat.finance/lender/market/0xc9499006a149c553d18171747ed19aa7c6dd19e2')
          } else {
            setDefiNativeURL(' ')
          }
        }
      })
    }
  }

  useEffect(() => {
    fetchAndSetCollateral()
  }, [poolAddr, serverApiBaseUrl, restAPIKey])

  const onSubmit = async () => {
    const data: DefiCollateralCTO = {
      defiType,
      otVaultAddr: poolAddr,
      uuid,
      defiVaultAddr,
      defiVaultSymbol,
      defiVaultName,
      transferWalletAddr,
      calibrationWalletAddr,
      liquidityAssetAddr,
      exchangeRateMaximumAutoChange,
      exchangeRateMinimumMaterialChange,
      resolvedVariance,
      defiChainId,
      nativeDefiExchangeRateScale,
      assetTolerance,
      feeVaultWalletAddr,
      defiNativeURL,
    }
    setIsSubmitting(true)
    // If maturityDate is blank, set to undefined
    const newCollateralSetCTO = {
      isDefi: true,
      poolAddr,
      collateral: [data],
      timestamp: Math.floor(Date.now() / 1000),
      exchangeRateAutomation,
    } as CollateralSetCTO
    await saveCollateral(serverApiBaseUrl, newCollateralSetCTO, cognitoUser)

    if (onClose) onClose()
    setIsSubmitting(false)
  }

  return (
    <Box sx={{ p: 2, position: 'relative' }}>
      <Button
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          minWidth: 'auto',
          p: 1,
        }}
      >
        ✕
      </Button>

      <SectionHeader>Edit Defi Collateral </SectionHeader>

      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12} sm={6}>
          <InputLabel id="defiType-label">Defi Type</InputLabel>
          <Select
            labelId="defiType-label"
            value={defiType}
            onChange={(e) => setDefiType(e.target.value as DefiType)}
            label="Defi Type"
            required
            fullWidth
          >
            <MenuItem value={DefiType.Undefined}>--- Select Defi Type ---</MenuItem>
            {Object.values(DefiType).map((defiType) => (
              <MenuItem key={defiType} value={defiType}>
                {defiType}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item xs={12} sm={6}>
          <InputLabel id="exchangeRateAutomation-label">Exchange Rate Automation</InputLabel>
          <Select
            labelId="exchangeRateAutomation-label"
            value={exchangeRateAutomation}
            onChange={(e) => setExchangeRateAutomation(e.target.value as ExchangeRateAutomation)}
            label="Exchange Rate Automation"
            required
            fullWidth
          >
            <MenuItem value={ExchangeRateAutomation.Manual}>Manual</MenuItem>
            <MenuItem value={ExchangeRateAutomation.FifteenMinutes}>Fifteen Minutes</MenuItem>
          </Select>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            value={transferWalletAddr}
            onChange={(e) => setTransferWalletAddr(e.target.value)}
            label="Transfer Wallet Addresses"
            required
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            value={defiVaultAddr}
            onChange={(e) => setDefiVaultAddr(e.target.value)}
            label="Defi Vault Address"
            required
            fullWidth
          />
        </Grid>{' '}
        <Grid item xs={12} sm={6}>
          <TextField
            value={calibrationWalletAddr}
            onChange={(e) => setCalibrationWalletAddr(e.target.value)}
            label="Calibration Wallet Address"
            required
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            value={liquidityAssetAddr}
            onChange={(e) => setLiquidityAssetAddr(e.target.value)}
            label="Liquidity Asset Address"
            required
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            value={feeVaultWalletAddr}
            onChange={(e) => setFeeVaultWalletAddr(e.target.value)}
            label="Fee Wallet Address"
            required
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            value={nativeDefiExchangeRateScale}
            onChange={(e) => setNativeDefiExchangeRateScale(e.target.value as ERC20Wei)}
            label="Scale of native and calibration exchange rates"
            required
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            value={exchangeRateMaximumAutoChange}
            onChange={(e) => setExchangeRateMaximumAutoChange(e.target.value as ERC20Wei)}
            label="Maximum Auto Change"
            required
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            value={exchangeRateMinimumMaterialChange}
            onChange={(e) => setExchangeRateMinimumMaterialChange(e.target.value as ERC20Wei)}
            label="Minimum Material Change"
            required
            fullWidth
          />
        </Grid>{' '}
        <Grid item xs={12} sm={6}>
          <TextField
            value={resolvedVariance}
            onChange={(e) => setResolvedVariance(e.target.value as ERC20Wei)}
            label="Native Variance"
            required
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            value={assetTolerance}
            onChange={(e) => setAssetTolerance(e.target.value as ERC20Wei)}
            label="Asset Tolerance"
            required
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField disabled={true} value={defiVaultSymbol} label="Symbol" fullWidth />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField disabled={true} value={defiVaultName} label="Name" fullWidth />
        </Grid>
        {/*
        <Grid item xs={12} sm={6}>
          <TextField
            {...register('marketValue', {
              required: 'Market Value is required',
              min: { value: 0, message: 'Market Value must be non-negative' },
            })}
            label="Market Value"
            required
            type="number"
            inputProps={{ step: '0.01' }}
            fullWidth
            error={!!errors.marketValue}
            helperText={errors.marketValue?.message}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            {...register('yieldOrCoupon', {
              required: 'Yield/Coupon is required',
              valueAsNumber: true,
              min: { value: 0, message: 'Yield/Coupon must be non-negative' },
            })}
            label="Yield/Coupon (%)"
            required
            type="number"
            inputProps={{ step: '0.01' }}
            fullWidth
            error={!!errors.yieldOrCoupon}
            helperText={errors.yieldOrCoupon?.message}
          />
        </Grid> */}
      </Grid>
      <Button
        onClick={() => {
          onSubmit().catch((error) => {
            console.error('Error saving defi collateral:', error)
          })
        }}
        variant="contained"
        fullWidth
        disabled={isSubmitting || isChangingType}
        sx={{ mt: 3, mb: 2 }}
      >
        Save Defi Collateral
      </Button>
    </Box>
  )
}
